import React from "react";
import { MdCheckCircle, MdLabel, MdFormatListNumbered, MdSettings, MdLightbulb, MdBuild, MdStar, MdSecurity, MdCode, MdTrendingUp, MdDevices, MdCloud, MdBusinessCenter, MdRocket, MdLink, MdSpeed, MdSearch } from "react-icons/md";

/**
 * BlogContentRenderer — Section-paired 2-column layout
 *
 * Groups content by emoji sections.
 * Pairs of adjacent sections are placed LEFT | RIGHT.
 * Orphan sections (odd) span full width.
 */

// ── Helpers ──────────────────────────────────────────────
const isEmojiHeading = (line) =>
  /^[🌐💡⚙️📈🔮📞🔑🚀🎯✅❗🛡️🔒✨🏆💎🔥⭐🎨📊📌📝🎙️🖥️🌍]/.test(line);

// Emoji to Icon mapping
const getIconForEmoji = (emoji) => {
  const iconMap = {
    '🌐': <MdDevices size={20} />,
    '💡': <MdLightbulb size={20} />,
    '⚙️': <MdSettings size={20} />,
    '📈': <MdTrendingUp size={20} />,
    '🔮': <MdStar size={20} />,
    '📞': <MdBusinessCenter size={20} />,
    '🔑': <MdSecurity size={20} />,
    '🚀': <MdRocket size={20} />,
    '✅': <MdCheckCircle size={20} />,
    '❗': <MdBuild size={20} />,
    '🛡️': <MdSecurity size={20} />,
    '🔒': <MdSecurity size={20} />,
    '✨': <MdStar size={20} />,
    '🏆': <MdStar size={20} />,
    '💎': <MdStar size={20} />,
    '🔥': <MdBuild size={20} />,
    '⭐': <MdStar size={20} />,
    '📊': <MdTrendingUp size={20} />,
    '📌': <MdLabel size={20} />,
    '📝': <MdLabel size={20} />,
    '🎙️': <MdBuild size={20} />,
    '🖥️': <MdCode size={20} />,
    '🌍': <MdCloud size={20} />,
    '❓': null, // No icon for question emoji
    '🤔': null, // No icon for thinking emoji
    '💻': <MdCode size={20} />,
    '🔧': <MdBuild size={20} />,
    '📱': <MdDevices size={20} />,
    '🌟': <MdStar size={20} />,
    '💼': <MdBusinessCenter size={20} />,
    '🎓': <MdStar size={20} />,
    '📚': <MdLabel size={20} />,
    '🔬': <MdBuild size={20} />,
    '🎬': <MdBuild size={20} />,
    '🎮': <MdCode size={20} />,
    '🔊': <MdBuild size={20} />,
    '📢': <MdBuild size={20} />,
    '📧': <MdBusinessCenter size={20} />,
    '🔗': <MdLink size={20} />,
    '📍': <MdLabel size={20} />,
    '⚡': <MdSpeed size={20} />,
    '🎁': <MdStar size={20} />,
    '🏢': <MdBusinessCenter size={20} />,
    '👥': <MdBusinessCenter size={20} />,
    '🤝': <MdBusinessCenter size={20} />,
    '💰': <MdStar size={20} />,
    '📉': <MdTrendingUp size={20} />,
    '🎯': <MdStar size={20} />,
    '🔍': <MdSearch size={20} />,
    '📋': <MdLabel size={20} />,
    '📁': <MdLabel size={20} />,
    '🗂️': <MdLabel size={20} />,
    '📎': <MdLabel size={20} />,
    '✏️': <MdLabel size={20} />,
    '🖌️': <MdCode size={20} />,
    '🎨': <MdCode size={20} />,
    '🎭': <MdStar size={20} />,
    '🎪': <MdStar size={20} />,
    '🎢': <MdStar size={20} />,
  };
  return iconMap[emoji] || null; // Return null if emoji not mapped (no icon shown)
};

// Remove emoji from heading and get icon
const parseHeading = (heading) => {
  // Match any emoji at the start and remove it completely
  const emojiMatch = heading.match(/^([\p{Emoji_Presentation}\p{Extended_Pictographic}])\s*(.*)/u);
  if (emojiMatch) {
    const icon = getIconForEmoji(emojiMatch[1]);
    // Always remove emoji from text, whether icon exists or not
    return {
      icon: icon,
      text: emojiMatch[2].trim()
    };
  }
  // Fallback: try to match any non-word character at start
  const fallbackMatch = heading.match(/^([^\w\s])\s*(.*)/);
  if (fallbackMatch) {
    const icon = getIconForEmoji(fallbackMatch[1]);
    return {
      icon: icon,
      text: fallbackMatch[2].trim()
    };
  }
  return { icon: null, text: heading };
};

const isNumberedItem = (line) => /^\d+\.\s/.test(line);
const isBullet = (line) => /^[-•]\s/.test(line);
const isShortLabel = (line) => line.endsWith(":") && line.length < 80;
const isTechTag = (line) =>
  line.length < 40 && !/[.?,]/.test(line) && /^[A-Z]/.test(line);

// ── Parse lines into grouped sections ────────────────────
function parseSections(content) {
  if (!content) return [];

  const lines = content.split("\n").map((l) => l.trim()).filter((l) => l !== "");
  const sections = [];
  let currentSection = null;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (isEmojiHeading(line)) {
      if (currentSection) sections.push(currentSection);
      currentSection = { heading: line, blocks: [] };
      i++;
      continue;
    }

    if (!currentSection) {
      currentSection = { heading: null, blocks: [] };
    }

    if (isNumberedItem(line)) {
      const num = line.match(/^(\d+)\./)[1];
      const title = line.replace(/^\d+\.\s/, "");
      const children = [];
      i++;

      while (
        i < lines.length &&
        !isEmojiHeading(lines[i]) &&
        !isNumberedItem(lines[i])
      ) {
        const sub = lines[i];
        if (isBullet(sub)) {
          children.push({ type: "bullet", text: sub.replace(/^[-•]\s/, "") });
        } else if (isShortLabel(sub)) {
          children.push({ type: "label", text: sub });
        } else if (isTechTag(sub) && sub.length < 30) {
          children.push({ type: "tag", text: sub });
        } else {
          children.push({ type: "para", text: sub });
        }
        i++;
      }

      currentSection.blocks.push({ type: "numbered", num, title, children });
      continue;
    }

    if (isBullet(line)) {
      const bullets = [];
      while (i < lines.length && isBullet(lines[i])) {
        bullets.push(lines[i].replace(/^[-•]\s/, ""));
        i++;
      }
      currentSection.blocks.push({ type: "bullet-list", items: bullets });
      continue;
    }

    if (isTechTag(line) && line.length < 30) {
      const tags = [];
      while (
        i < lines.length &&
        isTechTag(lines[i]) &&
        lines[i].length < 30
      ) {
        tags.push(lines[i]);
        i++;
      }
      if (tags.length > 0) {
        currentSection.blocks.push({ type: "tags", items: tags });
        continue;
      }
    }

    currentSection.blocks.push({ type: "para", text: line });
    i++;
  }

  if (currentSection) sections.push(currentSection);
  return sections;
}

// ── Sub-block renderers ───────────────────────────────────
const NumberedBlock = ({ num, title, children }) => {
  const paras = children.filter((c) => c.type === "para");
  const labels = children.filter((c) => c.type === "label");
  const bullets = children.filter((c) => c.type === "bullet");
  const tags = children.filter((c) => c.type === "tag");

  return (
    <div className="flex gap-3 mt-4">
      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#008300] text-white flex items-center justify-center text-sm font-bold shadow mt-0.5">
        <MdFormatListNumbered size={16} />
      </span>
      <div className="flex-1">
        <h4 className="font-bold text-gray-800 text-base">{title}</h4>
        {paras.map((p, i) => (
          <p key={i} className="text-gray-600 text-sm leading-relaxed mt-1">
            {p.text}
          </p>
        ))}
        {labels.map((l, i) => (
          <p key={i} className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-3 mb-1 flex items-center gap-1">
            <MdLabel size={10} />
            {l.text}
          </p>
        ))}
        {(bullets.length > 0 || tags.length > 0) && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {bullets.map((b, i) => (
              <span
                key={i}
                className="px-2.5 py-1 bg-white border border-[#008300]/25 text-[#008300] rounded-full text-xs font-medium flex items-center gap-1"
              >
                <MdCheckCircle size={12} />
                {b.text}
              </span>
            ))}
            {tags.map((t, i) => (
              <span
                key={i}
                className="px-2.5 py-1 bg-white border border-[#008300]/25 text-[#008300] rounded-full text-xs font-medium flex items-center gap-1"
              >
                <MdCheckCircle size={12} />
                {t.text}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const BulletListBlock = ({ items }) => (
  <ul className="mt-3 space-y-1.5">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
        <MdCheckCircle size={12} className="flex-shrink-0 mt-1.5 text-[#008300]" />
        {item}
      </li>
    ))}
  </ul>
);

const TagBlock = ({ items }) => (
  <div className="flex flex-wrap gap-2 mt-3">
    {items.map((tag, i) => (
      <span
        key={i}
        className="px-3 py-1 bg-white border border-[#008300]/25 text-[#008300] rounded-full text-xs font-semibold shadow-sm"
      >
        {tag}
      </span>
    ))}
  </div>
);

const ParaBlock = ({ text }) => (
  <p className="text-gray-600 text-sm leading-relaxed mt-2">{text}</p>
);

// ── Section Panel (one side: left or right) ───────────────
const SectionPanel = ({ section, accentLeft = true }) => {
  const { icon, text } = parseHeading(section.heading || '');
  
  return (
    <div className="flex flex-col h-full">
      {/* Heading bar */}
      {section.heading && (
        <div
          className={`flex items-center gap-3 px-5 py-4 border-b border-gray-200 ${
            accentLeft ? "border-l-4 border-l-[#008300]" : "border-r-4 border-r-[#008300]"
          }`}
        >
          {icon && <span className="text-[#008300]">{icon}</span>}
          <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-snug">
            {text}
          </h3>
        </div>
      )}

      {/* Content */}
      <div className="p-5 flex-1 flex flex-col gap-1">
        {section.blocks.map((block, i) => {
          if (block.type === "numbered")
            return <NumberedBlock key={i} {...block} />;
          if (block.type === "bullet-list")
            return <BulletListBlock key={i} items={block.items} />;
          if (block.type === "tags")
            return <TagBlock key={i} items={block.items} />;
          if (block.type === "para")
            return <ParaBlock key={i} text={block.text} />;
          return null;
        })}
      </div>
    </div>
  );
};

// ── Full-width single section ─────────────────────────────
const FullWidthSection = ({ section }) => {
  const { icon, text } = parseHeading(section.heading || '');
  
  return (
    <div>
      {section.heading && (
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 border-l-4 border-l-[#008300]">
          {icon && <span className="text-[#008300]">{icon}</span>}
          <h3 className="text-base sm:text-lg font-bold text-gray-900">{text}</h3>
        </div>
      )}
      <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {section.blocks.map((block, i) => {
          if (block.type === "numbered") return <NumberedBlock key={i} {...block} />;
          if (block.type === "bullet-list") return <BulletListBlock key={i} items={block.items} />;
          if (block.type === "tags") return <TagBlock key={i} items={block.items} />;
          if (block.type === "para")
            return <p key={i} className="col-span-2 text-gray-600 text-sm leading-relaxed">{block.text}</p>;
          return null;
        })}
      </div>
    </div>
  );
};

// ── Main Renderer ─────────────────────────────────────────
const BlogContentRenderer = ({ content }) => {
  const sections = parseSections(content);

  const rendered = [];
  let i = 0;

  while (i < sections.length) {
    const left = sections[i];

    // Plain text section (no emoji heading) — render inline, centered
    if (!left.heading) {
      rendered.push(
        <div key={i} className="mt-4 max-w-4xl mx-auto space-y-2">
          {left.blocks.map((block, bi) => {
            if (block.type === "para")
              return <p key={bi} className="text-gray-700 text-base leading-relaxed">{block.text}</p>;
            if (block.type === "bullet-list")
              return <BulletListBlock key={bi} items={block.items} />;
            if (block.type === "tags")
              return <TagBlock key={bi} items={block.items} />;
            return null;
          })}
        </div>
      );
      i += 1;
      continue;
    }

    const right = sections[i + 1];

    if (right && right.heading) {
      // Paired: render both as centered single columns
      rendered.push(
        <div key={i} className="mt-6 space-y-6">
          <div className="max-w-4xl mx-auto">
            <SectionPanel section={left} accentLeft={true} />
          </div>
          <div className="max-w-4xl mx-auto">
            <SectionPanel section={right} accentLeft={true} />
          </div>
        </div>
      );
      i += 2;
    } else {
      // Orphan: full width centered
      rendered.push(
        <div key={i} className="mt-6 max-w-4xl mx-auto">
          <FullWidthSection section={left} />
        </div>
      );
      i += 1;
    }
  }

  return <div className="space-y-2">{rendered}</div>;
};

export default BlogContentRenderer;
